import * as React from 'react';
import colors from '../../../../../../config/theme/colors';
import { fm } from 'Components/FormatMessage';
import InputHandler from 'Components/InputHandler';
import ActionButton from 'Components/ActionButton';
import { ClearIconStyled } from '..';
import type { IExtendedSearchField } from '../ExtendedSearchField';
import type { TSleepApneaESTreatmentType } from '../../definitions/sleepApnea';
import { TESSearchFields } from '../../definitions';

const TreatmentField = ({ searchFields, onChange, value }: IOwnProps): React.JSX.Element => {
  const onChangeTreatmentCriteria = (i: number) => (values: TOnChangeValues) => {
    const fieldName = Object.keys(values)[0];
    if (!fieldName) return;
    const fieldValue = values[fieldName];

    // Clone or initialize treatment criteria
    let newTreatmentCriteria: TSleepApneaESTreatmentType[] | 'noCropping' = (
      Array.isArray(searchFields.treatment) && searchFields.treatment.length > 0
        ? structuredClone(searchFields.treatment)
        : [{ treatment: null }]
    ) as TSleepApneaESTreatmentType[];

    const criteria: TSleepApneaESTreatmentType = newTreatmentCriteria[i] ?? {};

    // Handle treatment-specific logic
    if (fieldName === 'treatment' && (!Array.isArray(fieldValue) || fieldValue.length === 0)) {
      criteria[fieldName] = 'noCropping';
    } else if (fieldName === 'treatment') {
      criteria.treatment = fieldValue as TSleepApneaESTreatmentType['treatment'];
    } else if (fieldName === 'status') {
      criteria.status = fieldValue as TSleepApneaESTreatmentType['status'];
    }

    // Handle status logic based on treatment value
    if (
      fieldName === 'treatment' &&
      (!criteria.status ||
        !Array.isArray(fieldValue) ||
        fieldValue.length === 0 ||
        (fieldValue.length === 1 && fieldValue[0] === 'surgicalTreatment'))
    ) {
      criteria.status = 'noCropping';
    }

    // Special case
    if (
      fieldName === 'treatment' &&
      Array.isArray(newTreatmentCriteria) &&
      newTreatmentCriteria.length === 1 &&
      typeof newTreatmentCriteria[0] === 'object' &&
      (!('treatment' in newTreatmentCriteria[0]) ||
        !(newTreatmentCriteria[0] as TAnyObject).treatment ||
        (newTreatmentCriteria[0] as TAnyObject).treatment === 'noCropping')
    ) {
      newTreatmentCriteria = 'noCropping';
    }

    // Update the state with the modified treatment criteria
    onChange({ treatment: newTreatmentCriteria });
  };

  return (
    <React.Fragment>
      {(Array.isArray(searchFields.treatment) ? searchFields.treatment : [{ treatment: null }]).map((t, i, arr) => (
        <React.Fragment key={`searchCriteria${i}`}>
          <div
            style={{
              backgroundColor: colors.lightestGray,
              padding: '1rem',
              marginBottom: i === arr.length - 1 ? '1rem' : undefined,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ marginBottom: '1rem', color: colors.tertiaryText }}>
                {fm('extendedSearch.searchCriteriaTitle')}
                {` ${i + 1}`}
              </div>
              {arr.length > 1 && (
                <ClearIconStyled
                  onClick={() =>
                    Array.isArray(searchFields.treatment) &&
                    onChange({ treatment: searchFields.treatment.filter((_, index) => index !== i) })
                  }
                />
              )}
            </div>
            <InputHandler
              type="Checkbox"
              editing={true}
              name="treatment"
              formData={{
                onChange: (values: TOnChangeValues) => onChangeTreatmentCriteria(i)(values),
                document: { treatment: t.treatment },
              }}
              options={[
                'patientDoesNotWantRespiratorySupportTherapy',
                'oxygenTherapy',
                'hfncTherapy',
                'papTherapy',
                'madTherapy',
                'surgicalTreatment',
                'postureTreatment',
              ]}
              optionFormatter={(name: string | number): string | React.JSX.Element => fm(`extendedSearch.opts.${name}`)}
            />
            {t.treatment && Array.isArray(t.treatment) && (
              <React.Fragment>
                <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, margin: '1rem 0 1rem 0' }}>
                  {fm('extendedSearch.status')}
                </div>
                <InputHandler
                  type="Radio"
                  editing={true}
                  name="status"
                  formData={{
                    onChange: onChangeTreatmentCriteria(i),
                    document: { status: t.status },
                  }}
                  options={['noCropping', 'ongoing', 'ended']}
                  optionFormatter={(name: string | number): string | React.JSX.Element =>
                    fm(`extendedSearch.opts.${name}`)
                  }
                  disabledOptions={
                    t.treatment.length > 0 && !(t.treatment.length === 1 && t.treatment[0] === 'surgicalTreatment')
                      ? undefined
                      : ['noCropping', 'ongoing', 'ended']
                  }
                />
              </React.Fragment>
            )}
          </div>
          {arr.length > 1 && i < arr.length - 1 && (
            <div style={{ padding: '1rem', textTransform: 'uppercase' }}>{fm('general.and')}</div>
          )}
        </React.Fragment>
      ))}
      <div style={{ marginTop: '2rem', justifyContent: 'center' }}>
        <ActionButton
          text={'extendedSearch.addSearchCriteria'}
          onClick={() => {
            const newTreatment = structuredClone(
              Array.isArray(searchFields.treatment) && searchFields.treatment.length > 0
                ? searchFields.treatment
                : [{ treatment: null }],
            ) as (TSleepApneaESTreatmentType | { treatment: null })[];
            newTreatment.push({ treatment: null });
            onChange({ treatment: newTreatment });
          }}
          width={16}
          height={3}
          fontSize={16}
        />
      </div>
    </React.Fragment>
  );
};

interface IOwnProps extends Pick<IExtendedSearchField, 'searchFields' | 'onChange'> {
  value: TESSearchFields[keyof TESSearchFields];
}

export default TreatmentField;
